import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF, faTiktok, faLinkedinIn, faGoogle,
} from "@fortawesome/free-brands-svg-icons";
import {
  faSort, faSortUp, faSortDown, faSearch, faTimesCircle,
  faPause, faPlay, faSpinner, faExternalLinkAlt, faFileArrowDown,
  faChartBar, faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest, getCampaignInsightsChunked } from "services/api/apiService";
import { toast } from "react-toastify";

// ─── Constants ────────────────────────────────────────────────────

const PLATFORMS = [
  { key: "tiktok",   label: "TikTok",   icon: faTiktok,     iconColor: "text-black" },
  { key: "meta",     label: "Meta",      icon: faFacebookF,  iconColor: "text-blue-600" },
  { key: "linkedin", label: "LinkedIn",  icon: faLinkedinIn, iconColor: "text-blue-700" },
  { key: "snapchat", label: "Snapchat",  emoji: "👻" },
  { key: "google",   label: "Google",    icon: faGoogle,     iconColor: "text-red-500" },
  { key: "taboola",  label: "Taboola",   emoji: "📡" },
];

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 days" },
  { key: "30d",   label: "30 days" },
];

const STATUS_FILTERS = ["ALL", "ACTIVE", "PAUSED"];

const STATUS_CLASSES = {
  ACTIVE:  "bg-green-100 text-green-700",
  ENABLE:  "bg-green-100 text-green-700",
  PAUSED:  "bg-amber-100 text-amber-700",
  DISABLE: "bg-amber-100 text-amber-700",
};

// ─── Helpers ──────────────────────────────────────────────────────

const getDateRange = (preset) => {
  const end = new Date(), start = new Date();
  if (preset === "7d")  start.setDate(start.getDate() - 7);
  if (preset === "30d") start.setDate(start.getDate() - 30);
  const fmt = (d) => d.toISOString().split("T")[0];
  return { start: fmt(start), end: fmt(end) };
};

const fmtNum   = (n) => { if (n == null || n === "") return "—"; const x = Number(n); if (isNaN(x)) return "—"; if (x >= 1e6) return `${(x / 1e6).toFixed(1)}M`; if (x >= 1e3) return `${(x / 1e3).toFixed(1)}K`; return x.toLocaleString(); };
const fmtMoney = (n) => { if (n == null || n === "") return "—"; const x = parseFloat(n); return isNaN(x) ? "—" : `$${x.toFixed(2)}`; };
const fmtCTR   = (n) => { if (n == null || n === "") return "—"; const x = parseFloat(n); return isNaN(x) ? "—" : `${x.toFixed(2)}%`; };

const fmtBudget = (c, platform) => {
  if (platform === "tiktok")   return c.budget ? `$${Number(c.budget).toFixed(0)}${c.budgetMode === "BUDGET_MODE_DAY" ? "/day" : " life."}` : "—";
  if (platform === "meta")     return c.dailyBudget ? `$${(c.dailyBudget / 100).toFixed(0)}/day` : (c.lifetimeBudget ? `$${(c.lifetimeBudget / 100).toFixed(0)} life.` : "—");
  if (platform === "snapchat") return c.dailyBudgetMicro ? `$${(c.dailyBudgetMicro / 1e6).toFixed(0)}/day` : (c.lifetimeMicro ? `$${(c.lifetimeMicro / 1e6).toFixed(0)} life.` : "—");
  return "—";
};

const isActive     = (s) => ["ACTIVE", "ENABLE"].includes(s);
const isToggleable = (s) => ["ACTIVE", "ENABLE", "PAUSED", "DISABLE"].includes(s);

const matchesStatus = (status, filter) => {
  if (filter === "ALL")    return true;
  if (filter === "ACTIVE") return isActive(status);
  if (filter === "PAUSED") return status === "PAUSED" || status === "DISABLE";
  return false;
};

const platformCampaignUrl = (platform, campaignId, accountId) => {
  switch (platform) {
    case "meta":     return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${accountId || ""}&selected_campaign_ids=${campaignId}`;
    case "tiktok":   return `https://ads.tiktok.com/i18n/perf/campaign?campaign_id=${campaignId}`;
    case "linkedin": return `https://www.linkedin.com/campaignmanager/accounts/${accountId || ""}/campaigns/${campaignId}`;
    case "snapchat": return `https://ads.snapchat.com/manage/${accountId || ""}/campaigns/${campaignId}`;
    case "google":   return `https://ads.google.com/aw/campaigns?campaignId=${campaignId}`;
    case "taboola":  return `https://backstage.taboola.com/backstage/campaigns/${campaignId}`;
    default:         return null;
  }
};

// ─── Small Components ─────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cls = STATUS_CLASSES[status] || "bg-gray-100 text-gray-500";
  const label = status === "ENABLE" ? "ACTIVE" : status === "DISABLE" ? "PAUSED" : (status || "UNKNOWN");
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
};

const MetricCell = ({ value, loading }) =>
  loading
    ? <span className="inline-block w-10 h-3 rounded bg-gray-200 animate-pulse" />
    : <span className="text-xs text-gray-600">{value}</span>;

const SortIcon = ({ sortKey, sortConfig }) => {
  if (sortConfig.key !== sortKey) return <FontAwesomeIcon icon={faSort} className="text-white/30 text-[10px]" />;
  return <FontAwesomeIcon icon={sortConfig.dir === "asc" ? faSortUp : faSortDown} className="text-white text-[10px]" />;
};

const ResetFiltersButton = ({ onClick }) => (
  <button
    onClick={onClick}
    title="Reset filters"
    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition"
  >
    <FontAwesomeIcon icon={faTimesCircle} className="text-xs" />
    Reset
  </button>
);

// ─── Main Component ───────────────────────────────────────────────

const AdHistory = () => {
  const [platform, setPlatform]             = useState(null);
  const [campaigns, setCampaigns]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [metrics, setMetrics]               = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [datePreset, setDatePreset]         = useState("7d");
  const [statusFilter, setStatusFilter]     = useState("ALL");
  const [search, setSearch]                 = useState("");
  const [sortConfig, setSortConfig]         = useState({ key: null, dir: "asc" });
  const [togglingId, setTogglingId]         = useState(null);
  const [linkedInAccId, setLinkedInAccId]   = useState(null);
  const [connectedSet, setConnectedSet]     = useState(new Set());

  useEffect(() => {
    getRequest("/api/connected-platforms/all")
      .then(data => {
        const names = (Array.isArray(data) ? data : []).map(p => p.name.toLowerCase());
        setConnectedSet(new Set(names));
      })
      .catch(() => {});
  }, []);

  const hasInsights = platform === "tiktok" || platform === "meta";

  // ── Load insights ─────────────────────────────────────────────

  const loadInsights = useCallback(async (p, list, preset) => {
    if (!["tiktok", "meta"].includes(p) || !list.length) { setMetrics({}); return; }
    setMetricsLoading(true);
    try {
      const { start, end } = getDateRange(preset);
      const metricsMap = await getCampaignInsightsChunked(p, list.map(c => c.id), start, end);
      setMetrics(metricsMap);
    } catch { setMetrics({}); }
    finally { setMetricsLoading(false); }
  }, []);

  // ── Load campaigns ────────────────────────────────────────────

  const loadCampaigns = useCallback(async (p, liAccId, preset) => {
    setLoading(true);
    setCampaigns([]); setMetrics({}); setSearch(""); setStatusFilter("ALL"); setSortConfig({ key: null, dir: "asc" });
    try {
      let items = [];
      if (p === "tiktok") {
        const r = await getRequest("/api/ads/tiktok/campaigns");
        items = (r?.data || []).map(c => ({
          id: String(c.campaign_id), name: c.campaign_name || "—",
          status: (c.operation_status || c.status || "UNKNOWN").toUpperCase(),
          budget: c.budget || 0, budgetMode: c.budget_mode || "",
          accountId: null,
        }));
      } else if (p === "meta") {
        const r = await getRequest("/api/ads/meta/campaigns");
        items = (r?.data || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: (c.status || "UNKNOWN").toUpperCase(),
          dailyBudget: c.daily_budget ? parseInt(c.daily_budget) : 0,
          lifetimeBudget: c.lifetime_budget ? parseInt(c.lifetime_budget) : 0,
          accountId: c.account_id || null,
        }));
      } else if (p === "linkedin") {
        const r = await getRequest(`/api/ads/linkedin/campaign-groups?adAccountId=${liAccId}`);
        items = (r?.data?.items || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: (c.status || "UNKNOWN").toUpperCase(),
          accountId: liAccId,
        }));
      } else if (p === "snapchat") {
        const r = await getRequest("/api/ads/snapchat/campaigns");
        items = (r?.data || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: (c.status || "UNKNOWN").toUpperCase(),
          dailyBudgetMicro: c.daily_budget_micro || 0,
          lifetimeMicro: c.lifetime_spend_cap_micro || 0,
          accountId: null,
        }));
      } else if (p === "google") {
        const r = await getRequest("/api/ads/google/campaigns");
        items = (r?.data || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: c.status === "ENABLED" ? "ACTIVE" : (c.status || "UNKNOWN").toUpperCase(),
          accountId: null,
        }));
      } else if (p === "taboola") {
        const r = await getRequest("/api/ads/taboola/campaigns");
        items = (r?.campaigns || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: c.is_active ? "ACTIVE" : "PAUSED",
          accountId: null,
        }));
      }
      setCampaigns(items);
      await loadInsights(p, items, preset);
    } catch (err) {
      toast.error(err.message || "Failed to load campaigns.");
    } finally {
      setLoading(false);
    }
  }, [loadInsights]);

  const handleSelectPlatform = async (p) => {
    if (loading) return;
    setPlatform(p);
    if (p === "linkedin") {
      try {
        const r = await getRequest("/api/ads/linkedin/ad-accounts");
        const accId = r?.data?.selectedLinkedInAdvertiserId;
        if (!accId) { toast.error("No LinkedIn Ad Account configured. Go to Connected Platforms."); return; }
        setLinkedInAccId(accId);
        await loadCampaigns(p, accId, datePreset);
      } catch (err) { toast.error(err.message); }
    } else {
      await loadCampaigns(p, null, datePreset);
    }
  };

  const handleDatePreset = async (preset) => {
    setDatePreset(preset);
    if (platform && campaigns.length) await loadInsights(platform, campaigns, preset);
  };

  // ── Toggle status ─────────────────────────────────────────────

  const handleToggle = async (campaign) => {
    if (togglingId || !isToggleable(campaign.status)) return;
    const curr = isActive(campaign.status);
    setTogglingId(campaign.id);
    try {
      let newStatus;
      if (platform === "tiktok") {
        newStatus = curr ? "DISABLE" : "ENABLE";
        await postRequest(`/api/ads/tiktok/campaign/${campaign.id}/status`, { status: newStatus });
      } else if (platform === "meta") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/meta/campaign/${campaign.id}/status`, { status: newStatus });
      } else if (platform === "snapchat") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/snapchat/campaign/${campaign.id}/status`, { status: newStatus });
      } else if (platform === "linkedin") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/linkedin/ad-accounts/${linkedInAccId}/campaign-groups/${campaign.id}/status`, { status: newStatus });
      } else if (platform === "taboola") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/taboola/campaign/${campaign.id}/status`, { is_active: !curr });
      } else {
        toast.info("Status toggle not supported for this platform.");
        return;
      }
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, status: newStatus } : c));
      toast.success(`Campaign ${curr ? "paused" : "resumed"}.`);
    } catch (err) {
      toast.error(err.message || "Failed to update status.");
    } finally {
      setTogglingId(null);
    }
  };

  // ── Sorting ───────────────────────────────────────────────────

  const handleSort = (key) => setSortConfig(prev =>
    prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
  );

  // ── Filtered + sorted list ────────────────────────────────────

  const filtered = useMemo(() => {
    let list = campaigns.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) &&
      matchesStatus(c.status, statusFilter)
    );
    if (!sortConfig.key) return list;
    return [...list].sort((a, b) => {
      let av, bv;
      if (["spend", "impressions", "clicks", "ctr"].includes(sortConfig.key)) {
        av = parseFloat(metrics[a.id]?.[sortConfig.key] || 0);
        bv = parseFloat(metrics[b.id]?.[sortConfig.key] || 0);
      } else {
        av = (a[sortConfig.key] || "").toString().toLowerCase();
        bv = (b[sortConfig.key] || "").toString().toLowerCase();
      }
      return (av < bv ? -1 : av > bv ? 1 : 0) * (sortConfig.dir === "asc" ? 1 : -1);
    });
  }, [campaigns, search, statusFilter, sortConfig, metrics]);

  // ── CSV export ────────────────────────────────────────────────

  const handleExport = () => {
    const cols = ["ID", "Name", "Status", "Budget", "Spend", "Impressions", "Clicks", "CTR"];
    const rows = filtered.map(c => {
      const m = metrics[c.id] || {};
      const status = c.status === "ENABLE" ? "ACTIVE" : c.status === "DISABLE" ? "PAUSED" : c.status;
      return [
        c.id,
        `"${c.name.replace(/"/g, '""')}"`,
        status,
        `"${fmtBudget(c, platform)}"`,
        m.spend || "",
        m.impressions || "",
        m.clicks || "",
        m.ctr || "",
      ].join(",");
    });
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ads_${platform}_${datePreset}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  // ─── Render ───────────────────────────────────────────────────

  const colSpan = hasInsights ? 8 : 4;

  return (
    <div className="h-full flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faChartBar} className="text-[#221C51] text-xl" />
          <div>
            <h1 className="text-xl font-bold text-[#221C51]">Ads Manager</h1>
            <p className="text-xs text-gray-500">Performance overview across all your connected platforms.</p>
          </div>
        </div>
        {platform && campaigns.length > 0 && (
          <button
            onClick={handleExport}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            <FontAwesomeIcon icon={faFileArrowDown} />
            <span>Export CSV</span>
          </button>
        )}
      </div>

      {/* Platform Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {PLATFORMS.map(p => {
          const isActive = platform === p.key;
          const isConnected = connectedSet.has(p.key);
          return (
            <button
              key={p.key}
              onClick={() => handleSelectPlatform(p.key)}
              disabled={loading}
              title={!isConnected ? `${p.label} is not connected. Go to Platform Settings to connect it.` : p.label}
              className={`relative flex items-center space-x-1.5 px-4 py-2 rounded-xl text-sm font-medium transition border disabled:opacity-60 ${
                isActive
                  ? "bg-[#221C51] text-white border-[#221C51]"
                  : isConnected
                    ? "bg-white text-gray-600 border-gray-200 hover:border-[#221C51]/30"
                    : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
              }`}
            >
              {p.emoji
                ? <span className={`text-base leading-none ${!isConnected && !isActive ? "opacity-50" : ""}`}>{p.emoji}</span>
                : <FontAwesomeIcon icon={p.icon} className={isActive ? "text-white" : isConnected ? p.iconColor : "text-gray-300"} />}
              <span>{p.label}</span>
              {!isConnected && (
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={`text-xs ml-0.5 ${isActive ? "text-white/70" : "text-amber-400"}`}
                  title={`${p.label} is not connected`}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* No platform selected */}
      {!platform && (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          Select a platform above to view campaign performance.
        </div>
      )}

      {platform && (
        <>
          {/* Controls bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">

            {/* Date presets */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {DATE_PRESETS.map(d => (
                <button
                  key={d.key}
                  onClick={() => handleDatePreset(d.key)}
                  className={`px-3 py-1.5 font-medium transition ${
                    datePreset === d.key ? "bg-[#221C51] text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >{d.label}</button>
              ))}
            </div>

            {/* Status filter */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {STATUS_FILTERS.map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 font-medium transition ${
                    statusFilter === s ? "bg-[#221C51] text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >{s}</button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
              <input
                type="text"
                placeholder="Search campaigns…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-7 pr-7 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221C51]/20 w-44"
              />
              {search && (
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs cursor-pointer"
                  onClick={() => setSearch("")}
                />
              )}
            </div>

            {(search || statusFilter !== "ALL") && (
              <ResetFiltersButton onClick={() => { setSearch(""); setStatusFilter("ALL"); }} />
            )}

            <span className="text-xs text-gray-400 ml-auto">
              {filtered.length} campaign{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm space-x-2">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading campaigns…</span>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              No campaigns found for this platform.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-[#221C51] text-white text-xs">
                    <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort("name")}>
                      <span className="flex items-center gap-1">Name <SortIcon sortKey="name" sortConfig={sortConfig} /></span>
                    </th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Budget</th>
                    {hasInsights && (
                      <>
                        <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort("spend")}>
                          <span className="flex items-center gap-1">Spend <SortIcon sortKey="spend" sortConfig={sortConfig} /></span>
                        </th>
                        <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort("impressions")}>
                          <span className="flex items-center gap-1">Impressions <SortIcon sortKey="impressions" sortConfig={sortConfig} /></span>
                        </th>
                        <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort("clicks")}>
                          <span className="flex items-center gap-1">Clicks <SortIcon sortKey="clicks" sortConfig={sortConfig} /></span>
                        </th>
                        <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort("ctr")}>
                          <span className="flex items-center gap-1">CTR <SortIcon sortKey="ctr" sortConfig={sortConfig} /></span>
                        </th>
                      </>
                    )}
                    <th className="px-4 py-3 text-center font-medium">On/Off</th>
                    <th className="px-4 py-3 text-center font-medium">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="py-10 text-center text-gray-400 text-sm">
                        No campaigns match your filters.
                      </td>
                    </tr>
                  ) : filtered.map(c => {
                    const m = metrics[c.id] || {};
                    const url = platformCampaignUrl(platform, c.id, c.accountId || linkedInAccId);
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 font-medium text-[#221C51] max-w-[220px] truncate" title={c.name}>
                          {c.name}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={c.status} />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {fmtBudget(c, platform)}
                        </td>
                        {hasInsights && (
                          <>
                            <td className="px-4 py-3"><MetricCell value={fmtMoney(m.spend)} loading={metricsLoading} /></td>
                            <td className="px-4 py-3"><MetricCell value={fmtNum(m.impressions)} loading={metricsLoading} /></td>
                            <td className="px-4 py-3"><MetricCell value={fmtNum(m.clicks)} loading={metricsLoading} /></td>
                            <td className="px-4 py-3"><MetricCell value={fmtCTR(m.ctr)} loading={metricsLoading} /></td>
                          </>
                        )}
                        <td className="px-4 py-3 text-center">
                          {togglingId === c.id ? (
                            <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400 text-sm" />
                          ) : isToggleable(c.status) && platform !== "google" ? (
                            <button
                              onClick={() => handleToggle(c)}
                              title={isActive(c.status) ? "Pause" : "Resume"}
                              className={`p-1.5 rounded-lg transition text-xs ${
                                isActive(c.status)
                                  ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                                  : "bg-green-100 text-green-600 hover:bg-green-200"
                              }`}
                            >
                              <FontAwesomeIcon icon={isActive(c.status) ? faPause : faPlay} />
                            </button>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open in platform"
                              className="text-[#221C51]/40 hover:text-[#221C51] transition"
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm" />
                            </a>
                          ) : (
                            <span className="text-gray-200">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdHistory;
